import { NSARouterMakerResult } from "namirasoft-account-react";
import { PriceOperation, SortItem } from "namirasoft-core";
import { FailoverRow, InferencerType, LoadBalancerRow, ModelRow, NamirasoftInferenceMetaDatabase, RaceRow } from "namirasoft-inference";
import { NSBox, NSBoxDateRange, NSBoxEntity, NSBoxEnum, NSButtonBlue, NSChartBar, NSChartDoughnut, NSColumn, NSLabel, NSLoading, NSPanel, NSRow, NSSectionTitle, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef } from "react";
import UsageChart, { UsageMetric, UsageWinnerFilter } from "../components/UsageChart";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from "../InferenceLayout";
import { InferenceRouterProps } from "../InferenceRouterProps";

interface DashboardPageProps extends NSARouterMakerResult, InferenceRouterProps { }

interface UsageSummary
{
    count: number;
    total_token: number;
    total_token_input: number;
    total_token_output: number;
    total_cost: number;
    total_cost_input: number;
    total_cost_output: number;
}

interface DashboardPageState
{
    summary: UsageSummary | null;
    loading: boolean;
    inferencer_type: InferencerType | null;
}

function toDateString(d: Date): string
{
    let y = d.getFullYear();
    let m = String(d.getMonth() + 1).padStart(2, "0");
    let day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export default class DashboardPage extends Component<DashboardPageProps, DashboardPageState>
{
    NSBoxEnum_InferencerType = createRef<NSBoxEnum<InferencerType>>();
    NSBoxEntity_Model = createRef<NSBoxEntity<ModelRow>>();
    NSBoxEntity_LoadBalancer = createRef<NSBoxEntity<LoadBalancerRow>>();
    NSBoxEntity_Failover = createRef<NSBoxEntity<FailoverRow>>();
    NSBoxEntity_Race = createRef<NSBoxEntity<RaceRow>>();
    NSBoxDateRange_Period = createRef<NSBoxDateRange>();

    defaultStart: string;
    defaultEnd: string;

    constructor(props: DashboardPageProps)
    {
        super(props);

        let today = new Date();
        let weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 7);

        this.defaultStart = toDateString(weekAgo);
        this.defaultEnd = toDateString(today);

        this.state = { summary: null, loading: false, inferencer_type: null };

        this.onApply = this.onApply.bind(this);
    }

    override componentDidMount()
    {
        this.loadSummary();
    }

    onApply(onFinished: () => void)
    {
        this.loadSummary().finally(onFinished);
    }

    private getInferencerId(inferencer_type: InferencerType | null): string | null
    {
        switch (inferencer_type)
        {
            case InferencerType.MODEL: return this.NSBoxEntity_Model.current?.getValueOne(false) ?? null;
            case InferencerType.LOAD_BALANCER: return this.NSBoxEntity_LoadBalancer.current?.getValueOne(false) ?? null;
            case InferencerType.FAILOVER: return this.NSBoxEntity_Failover.current?.getValueOne(false) ?? null;
            case InferencerType.RACE: return this.NSBoxEntity_Race.current?.getValueOne(false) ?? null;
            default: return null;
        }
    }

    async loadSummary()
    {
        let inferencer_type: InferencerType | null;
        let inferencer_id: string | null;
        let started_at: string;
        let ended_at: string;
        try
        {
            let raw = this.NSBoxEnum_InferencerType.current?.getValueOne(false) ?? null;
            inferencer_type = raw ? (InferencerType[raw as keyof typeof InferencerType] ?? null) : null;
            inferencer_id = this.getInferencerId(inferencer_type);
            let period = this.NSBoxDateRange_Period.current?.getValue(false);
            started_at = period?.from.toISOString() ?? this.defaultStart;
            ended_at = period?.to.toISOString() ?? this.defaultEnd;
        }
        catch (error)
        {
            this.props.notifier.onError(error as Error);
            return;
        }

        this.setState({ loading: true });
        try
        {
            let res = await this.props.servers.inference.usage.Summary(inferencer_type, inferencer_id, started_at, ended_at);
            this.setState({ summary: res, loading: false });
        }
        catch
        {
            this.setState({ loading: false });
        }
    }

    renderStat(title: string, value: string)
    {
        return (
            <NSPanel style={{ padding: "16px", minWidth: "220px", flex: 1 }}>
                <NSLabel title={title} description={value} />
            </NSPanel>
        );
    }

    renderChartPanel(title: string, chart: JSX.Element)
    {
        return (
            <NSPanel style={{ padding: "16px", flex: 1, minWidth: "320px" }}>
                <NSColumn>
                    <NSLabel title={title} />
                    <div style={{ position: "relative", height: 280, width: "100%" }}>
                        {chart}
                    </div>
                </NSColumn>
            </NSPanel>
        );
    }

    private renderInferencerIdPicker(inference: NamirasoftInferenceMetaDatabase): JSX.Element | null
    {
        let width = NSBox.width.double;
        switch (this.state.inferencer_type)
        {
            case InferencerType.MODEL:
                return (
                    <NSBoxEntity<ModelRow>
                        key="picker-model"
                        ref={this.NSBoxEntity_Model}
                        {...this.props}
                        title="Model"
                        required={false}
                        multiple={false}
                        table_name="model"
                        table={inference.tables.model}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => new SortItem(inference.tables.model, inference.tables.model.columns.name, true)}
                        style={{ width }}
                    />
                );
            case InferencerType.LOAD_BALANCER:
                return (
                    <NSBoxEntity<LoadBalancerRow>
                        key="picker-load-balancer"
                        ref={this.NSBoxEntity_LoadBalancer}
                        {...this.props}
                        title="Load Balancer"
                        required={false}
                        multiple={false}
                        table_name="load_balancer"
                        table={inference.tables.load_balancer}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => new SortItem(inference.tables.load_balancer, inference.tables.load_balancer.columns.name, true)}
                        style={{ width }}
                    />
                );
            case InferencerType.FAILOVER:
                return (
                    <NSBoxEntity<FailoverRow>
                        key="picker-failover"
                        ref={this.NSBoxEntity_Failover}
                        {...this.props}
                        title="Failover"
                        required={false}
                        multiple={false}
                        table_name="failover"
                        table={inference.tables.failover}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => new SortItem(inference.tables.failover, inference.tables.failover.columns.name, true)}
                        style={{ width }}
                    />
                );
            case InferencerType.RACE:
                return (
                    <NSBoxEntity<RaceRow>
                        key="picker-race"
                        ref={this.NSBoxEntity_Race}
                        {...this.props}
                        title="Race"
                        required={false}
                        multiple={false}
                        table_name="race"
                        table={inference.tables.race}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => new SortItem(inference.tables.race, inference.tables.race.columns.name, true)}
                        style={{ width }}
                    />
                );
            default:
                return null;
        }
    }

    override render()
    {
        let { summary, loading } = this.state;
        let inference = new NamirasoftInferenceMetaDatabase(this.props.account.token_manager, this.props.notifier.onError);

        let tokenInput = summary?.total_token_input ?? 0;
        let tokenOutput = summary?.total_token_output ?? 0;
        let tokenTotal = summary?.total_token ?? 0;

        let costInput = PriceOperation.millicent_to_fiat(summary?.total_cost_input ?? 0, 4);
        let costOutput = PriceOperation.millicent_to_fiat(summary?.total_cost_output ?? 0, 4);
        let costTotal = PriceOperation.millicent_to_fiat(summary?.total_cost ?? 0, 4);

        let tokenMax = Math.max(tokenInput, tokenOutput, tokenTotal, 1) * 1.1;
        let costMax = Math.max(costInput, costOutput, costTotal, 1) * 1.1;

        return (
            <InferenceLayout {...this.props} action={{ description: InferenceInfo.dashboard }}>
                <NSSectionTitle title="Usage Dashboard">
                    <NSPanel style={{ padding: "16px", width: "100%" }}>
                        <NSRow style={{ flexWrap: "wrap", gap: 8 }}>
                            <NSBoxEnum<InferencerType>
                                ref={this.NSBoxEnum_InferencerType}
                                title="Inferencer Type"
                                required={false}
                                multiple={false}
                                getEnumObject={() => InferencerType}
                                onChanged={(box) =>
                                {
                                    let raw = box.getValueOne(false);
                                    let next = raw ? (InferencerType[raw as keyof typeof InferencerType] ?? null) : null;
                                    this.setState({ inferencer_type: next });
                                }}
                            />
                            {this.renderInferencerIdPicker(inference)}
                            <NSBoxDateRange
                                ref={this.NSBoxDateRange_Period}
                                title="Period"
                                required
                                defaultValue={{
                                    from: new Date(this.defaultStart),
                                    to: new Date(this.defaultEnd)
                                }}
                            />
                            <NSColumn style={{ alignContent: "center" }}>
                                <NSButtonBlue
                                    title="Apply"
                                    onClick={{ action: this.onApply, showLoading: true }}
                                />
                            </NSColumn>
                        </NSRow>
                    </NSPanel>

                    <NSSpace size={NSSpaceSizeType.NORMAL} />

                    {loading && <NSLoading />}

                    <NSRow style={{ flexWrap: "wrap", gap: "16px", width: "100%" }}>
                        {this.renderStat("Interactions", String(summary?.count ?? 0))}
                        {this.renderStat("Total Tokens", tokenTotal.toLocaleString())}
                        {this.renderStat("Input Tokens", tokenInput.toLocaleString())}
                        {this.renderStat("Output Tokens", tokenOutput.toLocaleString())}
                    </NSRow>

                    <NSSpace size={NSSpaceSizeType.SMALL} />

                    <NSRow style={{ flexWrap: "wrap", gap: "16px", width: "100%" }}>
                        {this.renderStat("Total Cost", `$${costTotal.toFixed(4)}`)}
                        {this.renderStat("Input Cost", `$${costInput.toFixed(4)}`)}
                        {this.renderStat("Output Cost", `$${costOutput.toFixed(4)}`)}
                        {this.renderStat(
                            "Avg Cost / Interaction",
                            summary && summary.count > 0
                                ? `$${(costTotal / summary.count).toFixed(4)}`
                                : "$0.0000"
                        )}
                    </NSRow>

                    <NSSpace size={NSSpaceSizeType.NORMAL} />

                    <NSRow style={{ flexWrap: "wrap", gap: "16px", width: "100%" }}>
                        {this.renderChartPanel(
                            "Token Usage",
                            <NSChartBar
                                labels={["Input", "Output", "Total"]}
                                dataset={[{ label: "Tokens", data: [tokenInput, tokenOutput, tokenTotal] }]}
                                range={{ min: 0, max: tokenMax }}
                                yGrid
                                height={280}
                                mode={"stacked"}
                            />
                        )}
                        {this.renderChartPanel(
                            "Cost (USD)",
                            <NSChartBar
                                labels={["Input", "Output", "Total"]}
                                dataset={[{ label: "Cost", data: [costInput, costOutput, costTotal] }]}
                                range={{ min: 0, max: costMax }}
                                yGrid
                                height={280}
                                mode={"stacked"}
                            />
                        )}
                    </NSRow>

                    <NSSpace size={NSSpaceSizeType.SMALL} />

                    <NSRow style={{ flexWrap: "wrap", gap: "16px", width: "100%" }}>
                        {this.renderChartPanel(
                            "Token Split (Input vs Output)",
                            <NSChartDoughnut
                                title=""
                                labels={["Input", "Output"]}
                                datas={[tokenInput, tokenOutput]}
                                backgroundColors={["#7583E6", "#3AA498"]}
                                style={{ height: 280, display: "flex", justifyContent: "center" }}
                            />
                        )}
                        {
                            this.renderChartPanel(
                                "Cost Split (Input vs Output)",
                                <NSChartDoughnut
                                    title=""
                                    labels={["Input", "Output"]}
                                    datas={[costInput, costOutput]}
                                    backgroundColors={["#DB7E46", "#8C4BCB"]}
                                    style={{ height: 280, display: "flex", justifyContent: "center" }}
                                />
                            )
                        }
                    </NSRow>

                    <NSSpace size={NSSpaceSizeType.NORMAL} />

                    <NSPanel style={{ padding: "16px", width: "100%" }}>
                        <UsageChart
                            {...this.props}
                            title="Requests"
                            default_metric={[UsageMetric.Count]}
                            default_winner_filter={UsageWinnerFilter.All}
                        />
                    </NSPanel>
                </NSSectionTitle>
            </InferenceLayout>
        );
    }
}